import React, { FunctionComponent } from "react";
import { Node, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { IDataColumnDefProps, ITextQuery, PickerInput } from "@plex/react-components";
import { INodeConfigPropertiesProps } from "../NodeTypes/PropertyTypeDefinitions";
import { getDataTypeFromSchema, getSchema } from "../NodeTypes/DataSchemas";
import { ISchemaPropertyPickerOptions } from "../NodeTypes/Base";
import {
  DocumentSchemaType,
  IDocumentObjectSchema,
  propertyIsCompositeIdentifier,
  propertyIsIdentifier
} from "../FlowDocument/FlowDocumentModel";
import { camelCaseToReadable } from "../Util/NodeUtil";
import { buildNodeInputsFromSchema, deleteEdgesFromNodes } from "../Util/EdgeUtil";

interface ISchemaPropertyItem {
  id: string;
  name: string;
  dataType: string;
}

export interface ISchemaPropertyPickerProps extends INodeConfigPropertiesProps {
  dialogTitle: string;
  displayName: string;
}

export const getPickerSchemaProperties = (node: Node, options: ISchemaPropertyPickerOptions) => {
  if (options?.schemaIdProperty) {
    const schemaId = node.data.nodeProperties[options.schemaIdProperty];
    const schema = getSchema(schemaId) as unknown as IDocumentObjectSchema;
    if (schema && schema.schemaType === DocumentSchemaType.object) {
      return Object.keys(schema.properties)
        .filter(
          (propertyName: string) => !options?.identifierOnly || propertyIsIdentifier(schema.properties[propertyName])
        )
        .map((propertyName: string) => {
          const schemaProperty = schema.properties[propertyName];
          if (!schemaProperty) {
            return { id: propertyName, name: propertyName, dataType: propertyName };
          }

          const readablePropertyName = camelCaseToReadable(propertyName);
          const itemName = schemaProperty.label ?? readablePropertyName;
          const propertySchemaId = schemaProperty.schema;
          let dataType = "";
          if (propertySchemaId) {
            const idLabelCompositePrefix = propertyIsCompositeIdentifier(schemaProperty) ? "Composite " : "";
            const identifierSuffix = propertyIsIdentifier(schemaProperty) ? ` (${idLabelCompositePrefix}ID)` : "";
            dataType = camelCaseToReadable(getDataTypeFromSchema(propertySchemaId)) + identifierSuffix;
          }
          return { id: propertyName, name: itemName, dataType: dataType };
        });
    }
  }

  return [];
};

export const SchemaPropertyPickerField: FunctionComponent<ISchemaPropertyPickerProps> = (props) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const options = props.options as ISchemaPropertyPickerOptions;
  let initialDisplay = "";
  if (options?.displayProperty) {
    initialDisplay = props.node!.data.nodeProperties[options.displayProperty];
  }
  let [{ pickerSelected, pickerData, searchText }, setPicker] = React.useState({
    pickerSelected: props.node?.data.nodeProperties[props.name]
      ? [
          {
            id: props.node?.data.nodeProperties[props.name],
            name: initialDisplay
          } as ISchemaPropertyItem
        ]
      : ([] as ISchemaPropertyItem[]),
    pickerData: [] as { id: string; name: string }[],
    searchText: ""
  });

  const handlePickerSearch = (query: ITextQuery, cols: Array<IDataColumnDefProps<ISchemaPropertyItem>>) => {
    const results: ISchemaPropertyItem[] = getPickerSchemaProperties(
      props.node!,
      props.options as ISchemaPropertyPickerOptions
    );

    let items: {
      id: string;
      name: string;
    }[];
    if (cols.length === 0) {
      items = results.filter((x) => (x.name || "").toLowerCase().includes(query.query.toLowerCase()));
    } else {
      items = results.filter((row: any) =>
        query.isMatch(cols.map((col) => (col.valueSelector ? String(col.valueSelector(row)) : "")))
      );
    }

    setPicker((x: any) => ({ ...x, pickerData: items }));
  };

  const handlePickerSearchTextChanged = (value: string) => {
    setPicker((x: any) => ({ ...x, searchText: value }));
  };

  const updateInputsFromSchema = (identifier: string | undefined) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(props.node!.type!)!;
    const schemaId = props.node.data.nodeProperties[options.schemaIdProperty];
    if (nodeDefinition.inputsSchemaProperties?.schemaPropertyName === options.schemaIdProperty) {
      deleteEdgesFromNodes([props.node.id], reactFlowInstance, updateNodeInternals, {
        outputEdges: false,
        inputEdges: true,
        controlEdges: false
      });
      if (!identifier) {
        props.node.data.nodeProperties.inputs = {};
      } else {
        props.node.data.nodeProperties.inputs = buildNodeInputsFromSchema(
          props.node,
          schemaId,
          nodeDefinition,
          reactFlowInstance,
          undefined,
          undefined,
          identifier
        );
      }
    }
  };

  return (
    <PickerInput<ISchemaPropertyItem>
      key={props.name + "_schema_picker"}
      dialogTitle="Schema Properties"
      initialWidth={900}
      disabled={false}
      selected={
        props.node!.data.nodeProperties[props.name]
          ? [
              {
                id: props.node!.data.nodeProperties[props.name],
                name: props.node!.data.nodeProperties[options.displayProperty]
              }
            ]
          : []
      }
      onSelectionChanged={(values: any) => {
        if (props.node!.data.nodeProperties[props.name] === values[0]?.id) {
          return;
        }

        if (values.length === 0) {
          // Do not allow clearing property unless schema is unselected/undefined.
          const schemaId = props.node.data.nodeProperties[options.schemaIdProperty];
          if (schemaId) {
            return;
          }

          setPicker((x) => ({ ...x, pickerSelected: [], searchText: "" }));
          [];
          props.node!.data.nodeProperties[props.name] = null;
          if (options?.displayProperty) {
            props.node!.data.nodeProperties[options.displayProperty] = null;
          }

          updateInputsFromSchema(values[0]?.id);

          if (props.onChange) {
            props.onChange();
          }

          props.forceNodeUpdate();

          return;
        }

        pickerSelected = values.length > 0 ? [values[0]] : [];
        setPicker((x) => ({
          ...x,
          pickerSelected: pickerSelected,
          searchText: ""
        }));
        props.node!.data.nodeProperties[props.name] = values[0].id;
        if (options?.displayProperty) {
          props.node!.data.nodeProperties[options.displayProperty] = values[0].name;
        }

        updateInputsFromSchema(values[0]?.id);

        if (props.onChange) {
          props.onChange();
        }

        props.forceNodeUpdate();
      }}
      keySelector={(row: ISchemaPropertyItem) => row?.id}
      displaySelector={(row: ISchemaPropertyItem) => row?.name}
      data={pickerData}
      onSearch={handlePickerSearch}
      searchDisabled={false}
      isOpen={false}
      multiSelect={false}
      maxDisplay={500}
      searchText={searchText}
      onSearchTextChange={handlePickerSearchTextChanged}
    >
      <PickerInput.Column<ISchemaPropertyItem>
        key="1"
        id={props.displayName}
        title="Name"
        valueSelector={(row: ISchemaPropertyItem) => row.id}
        searchable
        sortable
      >
        {(row: ISchemaPropertyItem) => row.name}
      </PickerInput.Column>
      <PickerInput.Column<ISchemaPropertyItem>
        key="1"
        id="dataType"
        title="Type"
        sortable
        valueSelector={(row: ISchemaPropertyItem) => row.id}
      >
        {(row: ISchemaPropertyItem) => row.dataType}
      </PickerInput.Column>
    </PickerInput>
  );
};
